import React, { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Button, Col, Row } from "reactstrap"
import axios from "axios"
import { api } from "../../utils/variables"
import TopAppCardM from "../MetaInsight/TopAppCardM"
import MaintenanceBadge from "../../components/Common/MaintenanceBadge"
import "./mostPopular.css"
import InsightsPageHeader from "../../components/MetaInsight/InsightsPageHeader"
import FilterPanel from "../../components/KeywordTrackingNewUIPage/FilterPanel"
import { useFilters } from "../../hooks/useFilters"
import { useUrlPlatform } from "../../hooks/useUrlPlatform"
import TopAppCardSkeleton from "./TopAppCardSkeleton"
import SEO from "../../components/Common/SEO"
import { PAGE_SEO } from "../../config/seoConfig"
import { generateCanonicalUrl, generateItemListStructuredData, generateSlug, applySEODate } from "../../utils/seoUtils"
import PlatformsApi from "../../services/platformsApi"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"

export default function MostPopularPage() {
  const { filterSections } = useFilters()
  const { trackEvent } = useAnalytics()
  const location = useLocation()
  const navigate = useNavigate()
  // Only set when reached via a Weekly Insights "View All" click — landing here
  // directly (sidebar link, bookmark, etc.) leaves this unset, so no back button shows.
  const cameFromWeeklyInsights = location.state?.from === 'weekly-insights'
  const [mostPopular, setMostPopular] = useState([])
  const [platforms, setPlatforms] = useState([])
  // Lives in the `?platform=` URL query param (defaulting to Meta) rather than
  // plain state, so it survives a round trip to an app's detail page and back,
  // and so a direct link can pre-select a platform.
  const [selectedPlatform, setSelectedPlatform] = useUrlPlatform('meta')
  const [platformsLoading, setPlatformsLoading] = useState(true)
  const [itemsToShow, setItemsToShow] = useState(8)
  const [showAll, setShowAll] = useState(false)
  const [loading, setLoading] = useState(true)
  const [selectedCardId, setSelectedCardId] = useState(null)

  // country/language and last-updated state
  const [countriesList, setCountriesList] = useState([])
  const [countryOptions, setCountryOptions] = useState([])
  const [countriesLoading, setCountriesLoading] = useState(true)
  const [mostPopularCountry, setMostPopularCountry] = useState(() => {
    try {
      return sessionStorage.getItem('mostPopular_selectedCountry') || 'US'
    } catch (e) {
      return 'US'
    }
  })
  const [mostPopularLanguage, setMostPopularLanguage] = useState(() => {
    try {
      return sessionStorage.getItem('mostPopular_selectedLanguage')
    } catch (e) {
      return null
    }
  })
  const [mostPopularLastUpdated, setMostPopularLastUpdated] = useState(null)
  const [mostPopularFilterPanelOpen, setMostPopularFilterPanelOpen] = useState(false)
  const [mostPopularAppliedFilters, setMostPopularAppliedFilters] = useState({})
  const popularCache = sessionStorage.getItem("popularMeta")
  const [sortedItems, setSortedItems] = useState([])
  const [isSorted, setIsSorted] = useState(false)
  const [rankSort, setRankSort] = useState(null)

  useEffect(() => {
    // load countries first, then fetch most popular with country/language
    const loadCountries = async () => {
      setCountriesLoading(true)
      setPlatformsLoading(true)
      let list = []
      try {
        const [countriesRes, fetchedPlatforms] = await Promise.all([
          axios.get(`${api}/countries/`),
          PlatformsApi.fetchPlatforms()
        ])
        const res = countriesRes  // keep rest of logic using `res`
        list = res.data?.data || []
        setCountriesList(list)
        setCountryOptions(list.map(c => ({ value: c.code, label: c.name })))
        setPlatforms(fetchedPlatforms || PlatformsApi.getDefaultPlatforms())
        setPlatformsLoading(false)
        const us = list.find(x => x.code === 'US')
        const defaultLocale = us && Array.isArray(us.locales) && us.locales.length > 0 ? us.locales[0] : null
        setMostPopularLanguage(prev => prev || defaultLocale)
      } catch (err) {
        console.error('Error loading countries:', err)
        setPlatforms(PlatformsApi.getDefaultPlatforms())
        setPlatformsLoading(false)
      } finally {
        setCountriesLoading(false)
        const initCountry = mostPopularCountry || (list && list.length > 0 ? list[0].code : 'US')
        const countryEntry = list.find(x => x.code === initCountry)
        const initLangFromList = countryEntry && Array.isArray(countryEntry.locales) && countryEntry.locales.length > 0 ? countryEntry.locales[0] : null
        const initLanguage = mostPopularLanguage || initLangFromList || null
        setMostPopularCountry(prev => prev || initCountry)
        setMostPopularLanguage(prev => prev || initLanguage)
        // restore persisted filters (survive route changes)
        let savedFilters = null
        try {
          const raw = sessionStorage.getItem('mostPopularAppliedFilters')
          savedFilters = raw ? JSON.parse(raw) : null
        } catch (err) {
          savedFilters = null
        }
        setMostPopularAppliedFilters(savedFilters || {})

        setLoading(true)
        if (popularCache && (!initCountry && !initLanguage)) {
          setMostPopular(JSON.parse(popularCache))
          setLoading(false)
        } else {
          fetchMostPopular(initCountry, initLanguage, 1, savedFilters || {})
        }
      }
    }

    loadCountries()
  }, [])

  // Country/language select rendering (flags, react-select styling) now lives in
  // InsightsPageHeader; this page only needs to resolve a language when the country
  // changes, so its fetch call gets the right locale.
  const handleMostPopularCountryChange = (code, langForCountry) => {
    setMostPopularCountry(code)
    setMostPopularLanguage(langForCountry)
    try {
      sessionStorage.setItem('mostPopular_selectedCountry', code)
      sessionStorage.setItem('mostPopular_selectedLanguage', langForCountry || '')
    } catch (e) { /* ignore */ }
    setLoading(true)
    fetchMostPopular(code, langForCountry, 1, mostPopularAppliedFilters)
  }

  const handleMostPopularLanguageChange = (val) => {
    setMostPopularLanguage(val)
    try {
      sessionStorage.setItem('mostPopular_selectedLanguage', val || '')
    } catch (e) { /* ignore */ }
    setLoading(true)
    fetchMostPopular(mostPopularCountry, val, 1, mostPopularAppliedFilters)
  }

  const handlePlatformChange = (platformId) => {
    setSelectedPlatform(platformId)
    fetchMostPopular(mostPopularCountry, mostPopularLanguage, 1, mostPopularAppliedFilters, platformId)
  }

  const fetchMostPopular = async (
    country = mostPopularCountry,
    language = mostPopularLanguage,
    page = 1,
    filters = mostPopularAppliedFilters,
    platform = selectedPlatform
  ) => {
    try {
      // Re-arm the skeleton placeholder for every fetch (platform switch included) —
      // not just the initial mount — so the grid doesn't sit stale while new data loads.
      setLoading(true)
      const params = {}
      if (country) params.country = country
      if (language) params.language = language
      if (page) params.page = page

      if (filters && typeof filters === 'object') {
        if (filters.price) params.price = Array.isArray(filters.price) ? filters.price.join(',') : filters.price
        if (filters.rating) params.rating = Array.isArray(filters.rating) ? filters.rating.join(',') : filters.rating
        if (filters.genre) params.genre = Array.isArray(filters.genre) ? filters.genre.join(',') : filters.genre
        if (filters.platform) params.platform = Array.isArray(filters.platform) ? filters.platform.join(',') : filters.platform
        if (filters.sort_by) params.sort_by = filters.sort_by
        if (filters.sort) params.sort_by = params.sort_by || filters.sort
        if (filters.bundle) params.bundle = Array.isArray(filters.bundle) ? filters.bundle.join(',') : filters.bundle
        if (filters.number_of_players) params.number_of_players = filters.number_of_players
      }

      console.info('fetchMostPopular called', { params })
      const token = localStorage.getItem("authToken")
      const headers = {}
      if (token) headers.Authorization = `Bearer ${token}`
      const res = await axios.get(`${api}/tracker/stats/${platform}/most_popular`, { params, headers })
      const games = res.data.games || []
      setMostPopular(games)
      try { sessionStorage.setItem("popularMeta", JSON.stringify(games)) } catch (e) { /* ignore */ }
      setMostPopularLastUpdated(new Date())
      trackEvent(ANALYTICS_EVENTS.FEATURE.WEEKLY_INSIGHT_VIEWED, {
        insight_type: ANALYTICS_EVENTS.WEEKLY_INSIGHT_TYPES.POPULAR,
        platform,
        country,
      })
    } catch (error) {
      console.error("Error fetching most popular:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleMostPopularFilterApply = (filters) => {
    setMostPopularAppliedFilters(filters)
    // persist filters so they survive route changes
    try { sessionStorage.setItem('mostPopularAppliedFilters', JSON.stringify(filters || {})) } catch (e) { /* ignore */ }
    setLoading(true)
    fetchMostPopular(mostPopularCountry, mostPopularLanguage, 1, filters)
    setMostPopularFilterPanelOpen(false)
  }

  const handleRemoveMostPopularFilter = (key) => {
    const newFilters = { ...(mostPopularAppliedFilters || {}) }
    if (Object.prototype.hasOwnProperty.call(newFilters, key)) {
      delete newFilters[key]
      setMostPopularAppliedFilters(newFilters)
      // persist updated filters and refetch with skeleton
      try { sessionStorage.setItem('mostPopularAppliedFilters', JSON.stringify(newFilters || {})) } catch (e) { /* ignore */ }
      setLoading(true)
      fetchMostPopular(mostPopularCountry, mostPopularLanguage, 1, newFilters)
    }
  }



  useEffect(() => {
    if (showAll) setItemsToShow(mostPopular.length)
    else setItemsToShow(8)
  }, [showAll, mostPopular.length])

  const handleCardClick = cardId => {
    setSelectedCardId(cardId)
  }

  const displayApps = isSorted ? sortedItems.slice(0, itemsToShow) : mostPopular.slice(0, itemsToShow)

  const parseRank = (app) => {
    if (!app) return Number.MAX_SAFE_INTEGER
    const candidates = [app.rank, app.position, app.ranking, app.rank_position, app.rankValue]
    for (let c of candidates) {
      if (c !== undefined && c !== null && c !== '' && !isNaN(Number(c))) return Number(c)
    }
    if (app.meta && (app.meta.rank !== undefined)) return Number(app.meta.rank)
    return Number.MAX_SAFE_INTEGER
  }

  const sortByRank = (order = 'asc') => {
    const arr = Array.isArray(mostPopular) ? [...mostPopular] : []
    arr.sort((a, b) => {
      const ra = parseRank(a)
      const rb = parseRank(b)
      return order === 'asc' ? (ra - rb) : (rb - ra)
    })
    setSortedItems(arr)
    setIsSorted(true)
  }

  const toggleRankSort = () => {
    const next = rankSort === 'asc' ? 'desc' : 'asc'
    setRankSort(next)
    sortByRank(next)
  }

  useEffect(() => {
    if (!rankSort) { setIsSorted(false); setSortedItems([]); return }
    sortByRank(rankSort)
  }, [mostPopular, rankSort])

  // Generate structured data for most popular apps
  const mostPopularStructuredData = generateItemListStructuredData(
    'Most Popular VR Apps',
    displayApps,
    (app) => ({
      '@type': 'SoftwareApplication',
      name: app?.name || 'VR App',
      url: `${window.location.origin}/xr/app/${generateSlug(app?.name || '')}?id=${app?.item_id}`,
      applicationCategory: 'GameApplication',
      operatingSystem: 'Meta Quest',
    })
  );

  // Disclaimer references whichever store is currently selected, rather than
  // always saying "Meta" regardless of the active platform tab.
  const platformLabel = PlatformsApi.getPlatformDisplayName(selectedPlatform, platforms)
  const platformIconUrl = PlatformsApi.getPlatformIcon(selectedPlatform, platforms)

  return (
    <React.Fragment>
      <SEO
        title={applySEODate(PAGE_SEO.mostPopular.title)}
        description={PAGE_SEO.mostPopular.description}
        keywords={PAGE_SEO.mostPopular.keywords}
        canonical={generateCanonicalUrl('/most-popular')}
        robots={PAGE_SEO.mostPopular.robots}
        structuredData={mostPopularStructuredData}
      />
      <div className="container-fluid mostpopular-page" style={{ maxWidth: "100vw", padding: 32 }}>
        <div className="category_track_innersec">
          <div className="row">
            <div className="col-sm-12">
              <InsightsPageHeader
                onBack={cameFromWeeklyInsights ? () => navigate('/weekly-insights') : undefined}
                title="Most Engaged"
                description="Dive into the most popular apps of the week, combining app score, downloads, and user engagement metrics."
                disclaimer={`Insights are based on ${platformLabel} Store rankings.`}
                platforms={platforms}
                platformsLoading={platformsLoading}
                selectedPlatform={selectedPlatform}
                onPlatformChange={handlePlatformChange}
                countriesList={countriesList}
                countryOptions={countryOptions}
                countriesLoading={countriesLoading}
                selectedCountry={mostPopularCountry}
                selectedLanguage={mostPopularLanguage}
                onCountryChange={handleMostPopularCountryChange}
                onLanguageChange={handleMostPopularLanguageChange}
                appliedFilters={mostPopularAppliedFilters}
                onRemoveFilter={handleRemoveMostPopularFilter}
                lastUpdated={mostPopularLastUpdated}
                onOpenFilters={() => setMostPopularFilterPanelOpen(true)}
                rankSort={rankSort}
                onToggleSort={toggleRankSort}
              />
              <FilterPanel
                open={mostPopularFilterPanelOpen}
                sections={filterSections.length > 0 ? filterSections : undefined}
                // Platform is already selectable via the Meta Quest/Steam tabs above -
                // don't duplicate it inside the filter panel.
                hidePlatformSection
                onClose={() => setMostPopularFilterPanelOpen(false)}
                onApply={handleMostPopularFilterApply}
                initialValues={mostPopularAppliedFilters}
              />
            </div>
          </div>

          <div className="mostpopular-content">
            {loading ? (
              <Row className="g-3">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Col key={index} xs={12} sm={6}>
                    <div className="mostpopular-card">
                      <TopAppCardSkeleton />
                    </div>
                  </Col>
                ))}
              </Row>
            ) : (
              <>
                {displayApps.length > 0 ? (
                  <Row className="g-3">
                    {displayApps.map((app, index) => (
                      <Col key={app?.item_id || index} xs={12} sm={6}>
                        <div
                          className={`mostpopular-card ${selectedCardId === app?.item_id ? "selected" : ""}`}
                          onClick={() => handleCardClick(app?.item_id)}
                        >
                          <TopAppCardM
                            gamedata={app}
                            priceLayout="discounts"
                            showEnds={false}
                            headingLevel="h2"
                            insightType={ANALYTICS_EVENTS.WEEKLY_INSIGHT_TYPES.POPULAR}
                            sourceSurface="most_popular_page"
                            platformIcon={platformIconUrl}
                            platformName={platformLabel}
                          />
                        </div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <p style={{ color: '#A1ACB8', fontSize: 16 }}>
                      {Object.keys(mostPopularAppliedFilters || {}).length > 0
                        ? 'No results matched with filter.'
                        : 'No results to display.'}
                    </p>
                  </div>
                )}

                {!loading && (
                  <div className="text-center mt-4">
                    {!showAll && mostPopular.length > 4 ? (
                      <Button className="see-more-btn" onClick={() => setShowAll(true)}>
                        See More
                      </Button>
                    ) : showAll ? (
                      <Button className="see-less-btn" onClick={() => setShowAll(false)}>
                        See Less
                      </Button>
                    ) : null}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}


